/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */
const FAQt_a = document.querySelectorAll('.disclosure');
const FAQt_panel = document.querySelectorAll('.disclosure .disclosure__panel');
FAQt_panel.forEach(panel =>{
  panel.style.maxHeight = `${panel.scrollHeight}px`;
});
FAQt_a.forEach(disclosure => {
  disclosure.querySelector('summary').addEventListener('click', function(event) {
    this.classList.toggle('active');
  });
});

// FAQt_a.forEach(summary => {
//   summary.addEventListener('click', function(event) {
//     event.preventDefault();
//     const disclosure = this.parentElement;
//     const panel = disclosure.querySelector('.disclosure__panel');
//     if (panel.style.maxHeight && panel.style.maxHeight !== '0px') {
//       panel.style.maxHeight = '0px';
//       setTimeout(() => {
//         disclosure.removeAttribute('open');
//       }, 300);
//     } else {
//       document.querySelectorAll('.disclosure').forEach(otherDisclosure => {
//         const otherPanel = otherDisclosure.querySelector('.disclosure__panel');
//         if (otherPanel.style.maxHeight && otherPanel.style.maxHeight !== '0px') {
//           otherPanel.style.maxHeight = '0px'; // Close other panels
      
//           setTimeout(() => {
//             otherDisclosure.removeAttribute('open');
//           }, 0);
//         }
//       });
//       // Open the clicked panel
//       panel.style.maxHeight = panel.scrollHeight + 'px'; // Set max-height to the content's scroll height
//       disclosure.setAttribute('open', '');
//     }
//   });
// });

function waitForElm(selector) {
    return new Promise(resolve => {
        if (document.querySelector(selector)) {
            return resolve(document.querySelector(selector));
        }

        const observer = new MutationObserver(mutations => {
            if (document.querySelector(selector)) {
                resolve(document.querySelector(selector));
                observer.disconnect();
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    });
}
document.addEventListener("DOMContentLoaded", function() {
});


document.addEventListener("DOMContentLoaded", function() {
  waitForElm('.font-selector-condition').then((elm) => {
      console.log('Element is ready');
      console.log(elm.textContent);
      const buttons = document.querySelectorAll(".font-selector-condition #fontSelector .button-wrapper-brd");
      const heading = document.querySelector(".font-selector-condition #myHeading");
      const textInput = document.querySelector(".font-selector-condition #textInput");
      const fontSelector = document.querySelector(".font-selector-condition #fontSelector");
      const textWrapper = document.querySelector(".font-selector-condition .iec-inner-text-wrapper");
      const fontPreview = document.querySelector(".font-selector-condition .fontPreview_FontPreview__4SFiv");
      const textInput_font = document.querySelector(".font-selector-condition #textInput-val");
      
      let currentFontFamilyN = "Laura";
      
      function applyFontFamily(fontFamily) {
        heading.style.fontFamily = fontFamily;
        currentFontFamilyN = fontFamily;
        
      }

      textInput.addEventListener('keyup', function(){
        textInput_font.value = textInput.value+', Skrifttype: '+document.querySelector('.button-wrapper-brd.selected .btn-i').getAttribute("data-name");
      });
      
      buttons.forEach(button => {
        button.addEventListener("click", function() {
          // Remove the 'selected' class from all buttons
          buttons.forEach(btn => btn.classList.remove("selected"));
      
          // Add the 'selected' class to the clicked button
          this.classList.add("selected");
      
          applyFontFamily(this.querySelector('.btn-i').getAttribute("value"));
          textInput_font.value = textInput.value+', Skrifttype: '+this.querySelector('.btn-i').getAttribute("data-name");
        });
      });
      
      textInput.addEventListener("input", function() {
        heading.textContent = textInput.value;
        heading.style.fontFamily = currentFontFamilyN;
      
        if (textInput.value.length === 0) {
          textWrapper.classList.add("full-width");
          textWrapper.classList.remove("partial-width");
          fontPreview.classList.remove("partial-width");
        } else {
          textWrapper.classList.remove("full-width");
          textWrapper.classList.add("partial-width");
          fontPreview.classList.add("partial-width");
        }
      });
      
      if (textInput.value.length === 0) {
        textWrapper.classList.add("full-width");
      } else {
        textWrapper.classList.add("partial-width");
        fontPreview.classList.add("partial-width");
      }
      
      applyFontFamily(currentFontFamilyN);
      
      const swiper = new Swiper('.selector.two.swiper', {
          slidesPerView: 3.5,
          spaceBetween: 7,
          loop: false,
        
          // Navigation arrows
          navigation: {
            nextEl: '[sel-next-2]',
            prevEl: '[sel-prev-2]',
          },
      });
  });

});
